import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo } from "./Logo";
import { LEGAL_NAV } from "@/data/legal";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(
        data.status === "already_subscribed"
          ? "You're already on the list."
          : "Welcome aboard — The NSW Planning Memo is on its way."
      );
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-teal-dark text-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Logo light />
            <p className="mt-6 text-ivory/70 text-sm leading-relaxed max-w-sm">
              Making NSW planning simple. Practical education, interactive toolkits and
              professional advice for homeowners, buyers and developers across New South Wales.
            </p>
            <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.24em] text-sand">
              Understand before you commit.
            </p>
            <div className="mt-8">
              <p className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                The NSW Planning Memo
              </p>
              <p className="mt-2 text-sm text-ivory/70">
                One practical planning briefing, once a month. No noise.
              </p>
              <form onSubmit={subscribe} className="mt-4 flex gap-2 max-w-sm">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-ivory/10 border border-ivory/20 rounded-full px-4 py-2.5 text-sm placeholder:text-ivory/40 focus:outline-none focus:border-sand transition-colors"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="newsletter-submit-button"
                  className="inline-flex items-center gap-1.5 bg-sand text-teal-dark text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-ivory transition-colors disabled:opacity-60"
                >
                  Join <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.24em] text-sand mb-4">Learn</p>
              <ul className="space-y-3 text-sm text-ivory/70">
                <li><Link className="hover:text-ivory transition-colors" to="/hub">Planning Hub</Link></li>
                <li><Link className="hover:text-ivory transition-colors" to="/rural">Rural Land</Link></li>
                <li><Link className="hover:text-ivory transition-colors" to="/da-approved">After Your DA Is Approved</Link></li>
                <li><Link className="hover:text-ivory transition-colors" to="/insights">Resources &amp; Insights</Link></li>
                <li><Link className="hover:text-ivory transition-colors" to="/toolkits">Planning Toolkits</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.24em] text-sand mb-4">Advisory</p>
              <ul className="space-y-3 text-sm text-ivory/70">
                <li><Link className="hover:text-ivory transition-colors" to="/book">Planner Connect — coming soon</Link></li>
                <li><Link className="hover:text-ivory transition-colors" to="/services">Planning Services</Link></li>
                <li><Link className="hover:text-ivory transition-colors" to="/contact">Contact &amp; FAQs</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.24em] text-sand mb-4">Studio</p>
              <ul className="space-y-3 text-sm text-ivory/70">
                <li><Link className="hover:text-ivory transition-colors" to="/about">About PLAN FIRST</Link></li>
                <li className="text-ivory/50">Sydney, New South Wales</li>
                <li className="text-ivory/50">hello@planfirstnsw.com.au</li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.24em] text-sand mb-4">Legal</p>
              <ul className="space-y-3 text-sm text-ivory/70">
                {LEGAL_NAV.map(([slug, label]) => (
                  <li key={slug}>
                    <Link className="hover:text-ivory transition-colors" to={`/legal/${slug}`} data-testid={`footer-legal-${slug}`}>
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-ivory/15">
          <p className="text-xs text-ivory/50 leading-relaxed max-w-4xl">
            PLAN FIRST NSW is an independent planning education and information platform. It is
            not affiliated with or endorsed by the NSW Government, the NSW Planning Portal or any
            local council, and does not issue approvals or certificates. All content, toolkits and
            AI-assisted reports are general educational resources — planning requirements vary by
            property and may change. Always confirm requirements with your local council, a
            registered certifier or an appropriately qualified professional before acting.{" "}
            <Link to="/legal/disclaimer" className="underline underline-offset-2 hover:text-ivory transition-colors">
              Read the full disclaimer
            </Link>
          </p>
          <div className="mt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-ivory/40">
            <span>© {new Date().getFullYear()} PLAN FIRST NSW. All rights reserved.</span>
            <span className="font-mono uppercase tracking-[0.2em]">
              Before You Buy. Before You Build. PLAN FIRST.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
